import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { convertToRaw, ContentState, EditorState } from 'wix-rich-content-editor-common';
import { getFeature } from './Features/helper';
import { IS_EMPTY_FEATURE, WORD_COUNT_FEATURE } from './Features';
import immutable from 'immutable';
import { attachImage } from '../DaEditor/plugins/plugin-image/modifiers';
import i18next from 'i18next';
/**
 * State we carry on the client
 *
 * @param raw
 * @param editorState
 * @param features
 */ export function convertToClientStorageState(rawContentState, contentState, featuresData) {
    return {
        editorText: {
            excerpt: '',
            html: {
                type: 'draft',
                markup: JSON.stringify(rawContentState),
                features: JSON.stringify(featuresData)
            }
        },
        html: ''
    };
}
export function convertToServerXhrPayload(rawContentState, contentState, featuresData) {
    const { editorText } = convertToClientStorageState(cleanEmptyAtomicBlocks(rawContentState), contentState, featuresData);
    return {
        editorRaw: editorText.html.markup
    };
}
export function textToPapiEditorText(text) {
    const contentState = ContentState.createFromText(text);
    const rawContentState = convertToRaw(contentState);
    return {
        excerpt: '',
        html: {
            type: 'draft',
            markup: JSON.stringify(rawContentState),
            features: '[]'
        }
    };
}
export function createWithDeviation(_param) {
    var { extended } = _param, deviation = _object_without_properties(_param, [
        "extended"
    ]);
    const text = EditorState.createWithContent(ContentState.createFromText(i18next.t('editor.shareToPost.text')));
    const editorState = attachImage(deviation, EditorState.moveSelectionToEnd(text));
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    return {
        excerpt: '',
        html: {
            type: 'draft',
            markup: JSON.stringify(rawContentState),
            features: '[]'
        }
    };
}
export function createWithDeviationAndText(_param, text = i18next.t('editor.shareToPost.text')) {
    var { extended } = _param, deviation = _object_without_properties(_param, [
        "extended"
    ]);
    const editorText = EditorState.createWithContent(ContentState.createFromText(text));
    const editorState = attachImage(deviation, EditorState.moveSelectionToEnd(editorText));
    const contentState = editorState.getCurrentContent();
    const rawContentState = convertToRaw(contentState);
    return {
        excerpt: '',
        html: {
            type: 'draft',
            markup: JSON.stringify(rawContentState),
            features: '[]'
        }
    };
}
export function getEmptyServerXhrPayload() {
    const emptyContentState = ContentState.createFromText('');
    const { editorText } = convertToClientStorageState(cleanEmptyAtomicBlocks(convertToRaw(emptyContentState)), emptyContentState, []);
    return {
        editorRaw: editorText.html.markup
    };
}
/**
 * Removes empty unstyled blocks from around atomics
 *
 * @param raw
 */ function cleanEmptyAtomicBlocks(raw) {
    const isEmpty = (block)=>{
        return block.text === '' && block.type === 'unstyled' && block.inlineStyleRanges.length === 0 && block.entityRanges.length === 0;
    };
    raw.blocks = raw.blocks.filter((block, idx)=>{
        const next = raw.blocks[idx + 1];
        const prev = raw.blocks[idx - 1];
        if (next && next.type === 'atomic' || prev && prev.type === 'atomic') {
            // Empty and NOT the first or last block
            // Stripping first/last block will break editor, viewer will hide these anyway.
            if (idx !== 0 && idx !== raw.blocks.length - 1 && isEmpty(block)) {
                return false;
            }
        }
        return true;
    });
    return raw;
}
/**
 * Test if the state has changed, note this does not check content
 * equality, so editing and undoing is seen as a change.
 *
 * @param prev
 * @param next
 */ export function hasChanged(prev, prevRaw, next, nextRaw) {
    if (!immutable.is(prev, next)) {
        return true;
    }
    try {
        if (JSON.stringify(prevRaw.entityMap) !== JSON.stringify(nextRaw.entityMap)) {
            return true;
        }
    } catch (e) {
    /* noop */ }
    return false;
}
export function isEditorTextEmpty(editorText) {
    let isEmpty;
    const isEmptyFeature = getFeature(IS_EMPTY_FEATURE, editorText.html.features);
    if (isEmptyFeature) {
        isEmpty = isEmptyFeature && isEmptyFeature.data.isEmpty;
    } else {
        const wordCountFeature = getFeature(WORD_COUNT_FEATURE, editorText.html.features);
        isEmpty = wordCountFeature && wordCountFeature.data.chars === 0;
    }
    return isEmpty;
}
export function isInsideEditor(element) {
    if (typeof document === 'undefined') {
        return;
    }
    const editors = document.querySelectorAll('.DraftEditor-root,[data-daeditor-retrieved-toolbar]');
    return Array.from(editors).some((editor)=>editor.contains(element));
}
export function elmoSaysThisMightBeADeviationUrl(url) {
    if (!url) {
        return;
    }
    /*
    1. :thumbcode: - the deviation id may come from a thumb code
    :thumb123456:
  */ let matches = url.match(/^:thumb(\d+):$/);
    if (matches) {
        return {
            id: parseInt(matches[1]),
            type: 'art'
        };
    }
    /*
    2. supebrowse link -- HAS TO GO FIRST because SB links can be appended to OTHER valid links
    URL scheme:   https://*.deviantart.com/*#/d*
    https://randomduck.deviantart.com/gallery/#/d2k5cau
  */ matches = url.match(/deviantart\.(com|lan).*#\/d([0-9a-z]+)/);
    if (matches) {
        return {
            id: parseInt(parseInt(matches[2], 36).toString(10)),
            type: 'art'
        };
    }
    /*
    3a. subdomainfree canonical link
    URL scheme:   https://www.deviantart.com/user/art/*(*)
    https://www.deviantart.com/randomduck/art/test-ignore-589-154773894?q=portal%3A%2F%2Fdeviations%2F%3Fcategory.order%3Dfresh%26gs.mode%3Dthumbs&qo=2
  */ matches = url.match(/deviantart\.(com|lan)\/((?:[0-9A-Za-z-_]+))\/((?:art|journal|status-update|commission|poll|tier))\/.*-(\d+)/);
    if (matches) {
        return {
            id: parseInt(matches[4]),
            type: matches[3] || 'art',
            username: matches[2]
        };
    }
    /*
    3b. canonical link
    URL scheme:   https://*.deviantart.com/art/*(*)
    https://randomduck.deviantart.com/art/test-ignore-589-154773894?q=portal%3A%2F%2Fdeviations%2F%3Fcategory.order%3Dfresh%26gs.mode%3Dthumbs&qo=2
  */ matches = url.match(/deviantart\.(com|lan)\/(?:art|journal)\/.*-(\d+)/);
    if (matches) {
        return {
            id: parseInt(matches[2]),
            type: 'art'
        };
    }
    /*
    4. fav.me  link
    URL scheme:   https://fav.me/d[devid] where devid is in base 36
    https://fav.me/d2k5cau

    URL scheme:   https://fav.me/[devid] where devid is in base 10
    https://fav.me/750477558
  */ matches = url.match(/fav\.me\/d([0-9a-z]+)/);
    if (matches) {
        return {
            id: parseInt(parseInt(matches[1], 36).toString(10)),
            type: 'art'
        };
    }
    matches = url.match(/fav\.me\/([0-9]+)/);
    if (matches) {
        return {
            id: parseInt(matches[1]),
            type: 'art'
        };
    }
    /*
    5. really old link
    URL scheme:   https://www.deviantart.com/deviation/*
    https://www.deviantart.com/deviation/154773894/
  */ matches = url.match(/www\.deviantart\.(com|lan)\/deviation\/(\d+)/);
    if (matches) {
        return {
            id: parseInt(matches[2]),
            type: 'art'
        };
    }
    /*
    6. really, really old link
    URL scheme:   https://www.deviantart.com/view/*
    https://www.deviantart.com/view/154773894/
  */ matches = url.match(/www\.deviantart\.(com|lan)\/view\/(\d+)/);
    if (matches) {
        return {
            id: parseInt(matches[2]),
            type: 'art'
        };
    }
    /*
    7. sta.sh short link
    URL scheme:   https://sta.sh/0*
    https://sta.sh/018bxsl3dm4o
  */ matches = url.match(/sta\.(sh|nk)\/0([0-9a-z]+)/);
    if (matches) {
        return {
            id: parseInt(parseInt(matches[2], 36).toString(10)),
            type: 'art'
        };
    }
    /*
    8. sta.sh short link
    URL scheme:   https://sta.sh/0*
    https://sta.sh/018bxsl3dm4o
  */ matches = url.match(/sta\.(sh|nk)\/1([0-9a-z]+)/);
    if (matches) {
        return {
            id: parseInt(matches[2]),
            type: 'art'
        };
    }
}
